/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddressConfirmation } from "../models/AddressConfirmation";
import type { OfficeAddressCreate } from "../models/OfficeAddressCreate";
import type { OrgEmailAddresses } from "../models/OrgEmailAddresses";
import type { PersonalAddress } from "../models/PersonalAddress";
import type { PersonalAddressUpdate } from "../models/PersonalAddressUpdate";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class EmailAddressesService {
  /**
   * Get Office Addresses
   * The organization's office addresses (every admin sees their mail), its
   * admins' personal addresses, and where office mailboxes forward.
   * @returns OrgEmailAddresses Successful Response
   * @throws ApiError
   */
  public static getOfficeAddresses({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<OrgEmailAddresses> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/email_addresses/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Add Office Address
   * Add a shared office address, on the main address's domain or a subdomain
   * of it, and send it the email that confirms it once it comes back, as
   * sent, through the office forward. One removed before comes back
   * confirmed if it was. Refused while Inbound Parse posts aren't checked.
   * @returns OrgEmailAddresses Successful Response
   * @throws ApiError
   */
  public static addOfficeAddress({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: OfficeAddressCreate;
  }): CancelablePromise<OrgEmailAddresses> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/email_addresses/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Personal Address
   * The signed-in admin's own personal address in the organization.
   * @returns PersonalAddress Successful Response
   * @throws ApiError
   */
  public static getPersonalAddress({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<PersonalAddress> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/email_addresses/organizations/{org_id}/me",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Set Personal Address
   * Set the signed-in admin's own personal address (empty clears it), and
   * email it the link that confirms it. Until then it isn't used. Mail that
   * comes in through it shows only to them. Setting one is refused while
   * Inbound Parse posts aren't checked; clearing always works.
   * @returns PersonalAddress Successful Response
   * @throws ApiError
   */
  public static setPersonalAddress({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: PersonalAddressUpdate;
  }): CancelablePromise<PersonalAddress> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/email_addresses/organizations/{org_id}/me",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Resend Personal Link
   * Email the signed-in admin's personal address a new confirmation link.
   * @returns PersonalAddress Successful Response
   * @throws ApiError
   */
  public static resendPersonalLink({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<PersonalAddress> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/email_addresses/organizations/{org_id}/me/resend",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Set Admin Personal Address
   * Set any admin's personal address (empty clears it), and email it the
   * link that confirms it. Only that admin can confirm it.
   * @returns OrgEmailAddresses Successful Response
   * @throws ApiError
   */
  public static setAdminPersonalAddress({
    orgId,
    accountId,
    requestBody,
  }: {
    orgId: number;
    accountId: string;
    requestBody: PersonalAddressUpdate;
  }): CancelablePromise<OrgEmailAddresses> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/email_addresses/organizations/{org_id}/admins/{account_id}",
      path: {
        org_id: orgId,
        account_id: accountId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Resend Admin Personal Link
   * Email an admin's personal address a new confirmation link.
   * @returns OrgEmailAddresses Successful Response
   * @throws ApiError
   */
  public static resendAdminPersonalLink({
    orgId,
    accountId,
  }: {
    orgId: number;
    accountId: string;
  }): CancelablePromise<OrgEmailAddresses> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/email_addresses/organizations/{org_id}/admins/{account_id}/resend",
      path: {
        org_id: orgId,
        account_id: accountId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        429: `Too Many Requests`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Remove Address
   * Stop using one of the organization's office addresses or any admin's
   * personal address. Undo puts it back.
   * @returns OrgEmailAddresses Successful Response
   * @throws ApiError
   */
  public static removeAddress({
    orgId,
    addressId,
  }: {
    orgId: number;
    addressId: number;
  }): CancelablePromise<OrgEmailAddresses> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/email_addresses/organizations/{org_id}/{address_id}",
      path: {
        org_id: orgId,
        address_id: addressId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Restore Address
   * Use a removed address again (Undo), confirmed if it was: an office
   * address, or the signed-in admin's own personal address (403 for
   * another's).
   * @returns OrgEmailAddresses Successful Response
   * @throws ApiError
   */
  public static restoreAddress({
    orgId,
    addressId,
  }: {
    orgId: number;
    addressId: number;
  }): CancelablePromise<OrgEmailAddresses> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/email_addresses/organizations/{org_id}/{address_id}/restore",
      path: {
        org_id: orgId,
        address_id: addressId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Resend Office Email
   * Send an office address waiting for confirmation a new confirmation email
   * (the one before stops working).
   * @returns OrgEmailAddresses Successful Response
   * @throws ApiError
   */
  public static resendOfficeEmail({
    orgId,
    addressId,
  }: {
    orgId: number;
    addressId: number;
  }): CancelablePromise<OrgEmailAddresses> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/email_addresses/organizations/{org_id}/{address_id}/resend",
      path: {
        org_id: orgId,
        address_id: addressId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Confirm Personal Address
   * Confirm a personal address from the link emailed to it: only while
   * signed in as the admin who added it.
   * @returns PersonalAddress Successful Response
   * @throws ApiError
   */
  public static confirmPersonalAddress({
    requestBody,
  }: {
    requestBody: AddressConfirmation;
  }): CancelablePromise<PersonalAddress> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/email_addresses/confirm",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
